import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, CreditCard, ExternalLink, Loader2, Minus, Plus, TriangleAlert, PlayCircle, Undo2, PauseCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { quote, money } from "@/lib/pricing";
import { PlanGapDialog } from "@/components/PlanGap";
import { CancelFlow } from "@/components/CancelFlow";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const UsageBar = ({ label, used, cap }) => {
  const unlimited = cap === null || cap === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(cap, 1)) * 100));
  const tone = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-400" : "bg-emerald-500";
  return (
    <div data-testid={`usage-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="tabular-nums text-slate-500">
          {used}{unlimited ? " · unlimited" : ` / ${cap}`}
        </span>
      </div>
      {!unlimited && (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-slate-100">
          <div className={`h-full rounded ${tone} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
};

export default function AdminBilling() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [interval, setIntervalKey] = useState(params.get("interval") === "annual" ? "annual" : "monthly");
  const [seats, setSeats] = useState(5);
  const [busy, setBusy] = useState(null);
  const autoBought = React.useRef(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/billing");
      setData(d);
      // Never default above what they actually need — a solo buyer seeing several seats is a price
      // shock. A seat count chosen on the pricing page wins.
      const asked = parseInt(params.get("seats"), 10);
      setSeats(Math.max(d.usage.users || 1, d.current.seats || 0, asked > 0 ? asked : 1));
      if (d.current.billing_interval && !params.get("interval")) setIntervalKey(d.current.billing_interval);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load billing");
    }
  };

  useEffect(() => { load(); }, []);

  // A "Buy now" click from the pricing page arrives with ?buy=1 — open Stripe once, then drop the
  // flag so a back-navigation or refresh does not fire a second session.
  useEffect(() => {
    if (!data || autoBought.current || params.get("buy") !== "1") return;
    const plan = params.get("plan");
    const sellable = data.catalogue.plans.find((p) => p.key === plan && !p.quote_only);
    autoBought.current = true;
    params.delete("buy");
    setParams(params, { replace: true });
    if (sellable && !data.current.subscribed && data.current.status !== "licensed") {
      checkout(sellable.key);
    }
  }, [data]);

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data: res } = await api.post("/billing/portal", { return_url: window.location.origin });
      window.location.href = res.portal_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      if (current.subscribed) {
        // Changing an existing subscription must never create a second one.
        await api.post("/billing/change", { plan, interval, seats });
        await load();
        toast.success("Your subscription has been updated");
        setBusy(null);
        return;
      }
      const { data: res } = await api.post("/billing/checkout", {
        plan, interval, seats, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  if (!data) return <Shell title="Billing"><Empty testId="billing-loading" text="Loading…" /></Shell>;

  const { catalogue, current, usage, limits } = data;
  const annual = interval === "annual";

  return (
    <Shell
      testId="admin-billing-page"
      title="Plan and Billing"
      subtitle="Your current plan, what you are using it for, and how to change it. Prices exclude any sales tax added at checkout."
    >
      <div className="space-y-6">
        <div className="panel p-5" data-testid="billing-current">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="overline-label">Current plan</span>
              <p className="font-display text-2xl font-semibold text-slate-900">
                {catalogue.plans.find((p) => p.key === current.effective_plan)?.name || current.effective_plan}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {current.status === "licensed"
                  ? "Licensed workspace — full access with no subscription or billing required."
                  : current.trial
                  ? `Trial — ${current.days_remaining} day${current.days_remaining === 1 ? "" : "s"} of full Professional access remaining.`
                  : current.subscribed
                    ? `${current.seats} seat${current.seats === 1 ? "" : "s"}, billed ${current.billing_interval === "annual" ? "annually" : "monthly"}.`
                    : "No subscription yet."}
              </p>
              {current.paused_until && (
                <p data-testid="billing-paused-note"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800">
                  <PauseCircle size={13} /> Paused — nothing is charged until{" "}
                  {String(current.paused_until).slice(0, 10)}. The workspace stays fully editable.
                </p>
              )}
              {current.cancel_at_period_end && (
                <p data-testid="billing-cancelling-note"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-800">
                  <TriangleAlert size={13} /> Cancelling — you keep full access until{" "}
                  {current.renews_at
                    ? new Date(current.renews_at * 1000).toISOString().slice(0, 10)
                    : "the end of the paid period"}
                  . Nothing is deleted.
                </p>
              )}
            </div>
            {current.read_only && (
              <p className="flex items-center gap-2 rounded-sm bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800">
                <TriangleAlert size={14} /> This workspace is read-only. Choose a plan to start editing again.
              </p>
            )}
            {current.subscribed && (
              <div className="flex flex-wrap items-center gap-2">
                {current.paused_until ? (
                  <Button
                    data-testid="billing-resume-btn"
                    disabled={busy === "resume"}
                    onClick={async () => {
                      setBusy("resume");
                      try {
                        await api.post("/billing/resume");
                        await load();
                        toast.success("Billing resumed — welcome back");
                      } catch (e) {
                        toast.error(apiError(e.response?.data?.detail));
                      } finally {
                        setBusy(null);
                      }
                    }}
                    className="gap-1.5"
                    style={{ backgroundColor: "var(--brand-primary)" }}
                  >
                    {busy === "resume" ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
                    Resume billing
                  </Button>
                ) : current.cancel_at_period_end ? (
                  <Button
                    data-testid="billing-keep-btn"
                    disabled={busy === "keep"}
                    onClick={async () => {
                      setBusy("keep");
                      try {
                        await api.post("/billing/keep", {});
                        await load();
                        toast.success("Cancellation reversed — your plan continues as normal");
                      } catch (e) {
                        toast.error(apiError(e.response?.data?.detail));
                      } finally {
                        setBusy(null);
                      }
                    }}
                    className="gap-1.5"
                    style={{ backgroundColor: "var(--brand-primary)" }}
                  >
                    {busy === "keep" ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
                    Keep my subscription
                  </Button>
                ) : (
                  <CancelFlow current={current} onDone={load} />
                )}
                <Button
                  variant="outline"
                  data-testid="billing-portal-btn"
                  disabled={busy === "portal"}
                  onClick={openPortal}
                  className="gap-1.5"
                >
                  {busy === "portal" ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                  Manage billing
                </Button>
              </div>
            )}
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <UsageBar label="Users" used={usage.users} cap={limits.users} />
            <UsageBar label="Projects" used={usage.projects} cap={limits.projects} />
            <UsageBar label="Programmes" used={usage.programmes} cap={limits.programmes} />
            <UsageBar label="Portfolios" used={usage.portfolios} cap={limits.portfolios} />
          </div>
        </div>

        {current.status !== "licensed" && (
        <div className="panel p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">Choose a plan</h3>
              <p className="mt-1 text-sm text-slate-600">
                Seats can be changed later. Annual billing is charged for twelve months up front at the lower rate.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Seats</span>
                <div className="flex items-center rounded-sm border border-slate-200">
                  <button data-testid="seats-minus" className="px-2 py-1.5 text-slate-500 hover:text-slate-900"
                    onClick={() => setSeats((s) => Math.max(usage.users || 1, s - 1))}>                    <Minus size={13} />
                  </button>
                  <span data-testid="seats-value" className="w-10 text-center text-sm font-semibold tabular-nums">{seats}</span>
                  <button data-testid="seats-plus" className="px-2 py-1.5 text-slate-500 hover:text-slate-900"
                    onClick={() => setSeats((s) => Math.min(500, s + 1))}>
                    <Plus size={13} />
                  </button>
                </div>
              </div>
              <div className="flex rounded-sm border border-slate-200 p-0.5">
                {[["monthly", "Monthly"], ["annual", "Annual"]].map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`interval-${key}`}
                    onClick={() => setIntervalKey(key)}
                    className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${
                      interval === key ? "bg-slate-900 text-white" : "text-slate-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {catalogue.plans.map((plan) => {
              const price = annual ? plan.price_annual : plan.price_monthly;
              const isCurrent = current.plan === plan.key && current.subscribed;
              // Show what this plan would actually charge for the chosen quantity.
              const q = plan.quote_only ? null : quote(plan, annual, seats);
              const planSeats = q ? q.seats : Math.max(seats, plan.min_seats || 1);
              // The upgrade decision, put where the decision is actually made.
              const upgrade = catalogue.plans.find((x) => x.recommended && x.key !== plan.key);
              return (
                <div
                  key={plan.key}
                  data-testid={`billing-plan-${plan.key}`}
                  className={`flex flex-col rounded-lg border p-5 ${
                    plan.recommended ? "border-[color:var(--brand-primary)] ring-1 ring-[color:var(--brand-primary)]" : "border-slate-200"
                  }`}
                >
                  <p className="font-display text-lg font-semibold text-slate-900">{plan.name}</p>
                  <p className="mt-1 text-xs text-slate-500">{plan.tagline}</p>
                  <p className="mt-4">
                    {plan.quote_only ? (
                      <span className="font-display text-2xl font-semibold text-slate-900">POA</span>
                    ) : (
                      <>
                        <span className="font-display text-3xl font-semibold text-slate-900">£{price}</span>
                        <span className="text-xs text-slate-500"> per user / month</span>
                      </>
                    )}
                  </p>
                  {!plan.quote_only && (
                    <>
                      <p className="mt-1 text-xs text-slate-500">
                        {planSeats} seat{planSeats === 1 ? "" : "s"}
                        {planSeats > seats ? ` (minimum for ${plan.name})` : ""} · {money(q.total)}{" "}
                        {annual ? "per year" : "per month"} + tax
                      </p>
                      {q.discountPct > 0 && (
                        <p data-testid={`billing-saving-${plan.key}`}
                          className="mt-1 inline-block bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                          {q.discountPct}% volume discount applied
                        </p>
                      )}
                    </>
                  )}
                  <ul className="mt-4 flex-1 space-y-1.5">
                    {plan.headline_points.slice(0, 5).map((point) => (
                      <li key={point} className="flex gap-2 text-xs text-slate-600">
                        <Check size={12} className="mt-0.5 shrink-0 text-[color:var(--brand-primary)]" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  {plan.quote_only ? (
                    <Button variant="outline" className="mt-5 w-full" data-testid="billing-enterprise-btn"
                      onClick={() => { window.location.href = "/pricing#enterprise"; }}>
                      Talk to us
                    </Button>
                  ) : (
                    <Button
                      data-testid={`billing-checkout-${plan.key}`}
                      disabled={!!busy || isCurrent}
                      onClick={() => checkout(plan.key)}
                      className="mt-5 w-full gap-1.5"
                      style={{ backgroundColor: "var(--brand-primary)" }}
                    >
                      {busy === plan.key ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                      {isCurrent ? "Current plan" : current.subscribed ? "Switch to this plan" : "Buy now"}
                    </Button>
                  )}
                  {upgrade && !plan.quote_only && (
                    <PlanGapDialog
                      matrix={catalogue.matrix}
                      from={plan}
                      to={upgrade}
                      onUpgrade={() => checkout(upgrade.key)}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Payments are processed by Stripe. Card details are entered with Stripe and never reach our servers.
            Minimum seats apply per plan and we will never reduce you below your current user count.
          </p>
        </div>
        )}
      </div>
    </Shell>
  );
}
